import React from 'react';
import { ExternalLink, Building2 } from 'lucide-react';
import { projects } from '../../data/projects';

const ProjectsSection: React.FC = () => {
    const getProjectLinks = (project: (typeof projects)[number]) => {
        const links = [];
        if (project.playStore && project.playStore !== '#') {
            links.push({ href: project.playStore, label: 'Play Store' });
        }
        if (project.appStore && project.appStore !== '#') {
            links.push({ href: project.appStore, label: 'App Store' });
        }
        if (project.website) {
            links.push({ href: project.website, label: 'Website' });
        }
        if (project.behance && project.behance !== '#') {
            links.push({ href: project.behance, label: 'Behance' });
        }
        return links;
    };

    return (
        <section id="projects" className="py-20 bg-ink-850/70">
            <div className="max-w-7xl mx-auto px-4">
                <div className="text-center mb-16">
                    <h2 className="spotlight inline-block text-3xl sm:text-4xl md:text-5xl font-bold mb-6 pb-2 leading-tight gradient-text bg-gradient-to-r from-emerald-400 to-gold-400 animate-fade-in-up">
                        Featured Projects
                    </h2>
                    <p
                        className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto animate-fade-in-up"
                        style={{ animationDelay: '0.2s' }}
                    >
                        Full-stack solutions spanning backends, APIs, and production
                        mobile applications
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {projects.map((project, index) => {
                        const links = getProjectLinks(project);

                        return (
                            <article
                                key={index}
                                className="lit-card group flex flex-col rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 animate-fade-in-up"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <div className="relative h-48 overflow-hidden bg-ink-950">
                                    <img
                                        src={project.image}
                                        alt={`${project.title} — ${project.company}`}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 via-transparent to-transparent" />
                                    <div className="absolute inset-0 ring-1 ring-inset ring-white/5 group-hover:ring-emerald-400/30 transition-colors duration-300" />
                                    <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold bg-ink-900/70 text-emerald-200 border border-emerald-400/30 backdrop-blur-sm">
                                        {project.year}
                                    </span>
                                </div>

                                <div className="flex flex-col flex-1 p-5">
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <h3 className="text-lg font-bold text-white leading-snug group-hover:text-emerald-300 transition-colors duration-300">
                                            {project.title}
                                        </h3>
                                        {links.length > 0 && (
                                            <div className="flex gap-1.5 shrink-0">
                                                {links.map((link) => (
                                                    <a
                                                        key={link.label}
                                                        href={link.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1.5 rounded-lg bg-white/5 text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                                                        title={link.label}
                                                    >
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-1.5 text-slate-400 text-xs mb-3">
                                        <Building2 className="w-3.5 h-3.5 shrink-0" />
                                        <span>{project.company}</span>
                                    </div>

                                    <p className="text-slate-300 text-sm leading-relaxed mb-4 line-clamp-3 flex-1">
                                        {project.description}
                                    </p>

                                    <div className="flex flex-wrap gap-1.5 pt-4 border-t border-white/5">
                                        {project.tech.map((tech, techIndex) => (
                                            <span
                                                key={techIndex}
                                                className="px-2 py-1 text-[11px] font-medium text-emerald-300/90 bg-emerald-500/10 rounded-md border border-emerald-500/20 group-hover:border-emerald-400/40 transition-colors"
                                            >
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default ProjectsSection;
